/**
 * Recebíveis de assinaturas (admin → Cobrança → "Assinaturas", issue #78).
 *
 * NÃO confundir com `finance.ts` (Financeiro INTERNO da empresa, épico #72:
 * `finance_entries`, caixa próprio). Aqui é **receita de clientes**: o que a
 * operação vai receber (previsto) e já recebeu (realizado) de assinaturas
 * recorrentes, semana a semana, do início do app até as renovações futuras.
 *
 * Fonte de dados: `tenants/{id}/billing/subscription` (previsto),
 * `tenants/{id}/payments` (realizado) e `tenants/{id}/invoices` (em aberto).
 * PSP-agnóstico: a agregação lê Firestore, não a API do PSP — quando entrarem
 * outros PSPs além do Stripe, seus webhooks gravam os mesmos docs e entram
 * automaticamente. Dinheiro sempre em **cents inteiros** (memória
 * `money_cents_single_source`); sem FX — tudo separado por moeda (memória
 * `currencies_locked_3`).
 */

import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */

export const ComputeSubscriptionReceivablesInputSchema = z.object({
  /**
   * Início da janela (epoch ms, inclusive). Omitido = primeira semana com
   * dado (servidor resolve pelo menor `paid_at`/`started_at`/`due_at`).
   */
  from: z.number().int().optional(),
  /**
   * Fim da janela (epoch ms, inclusive). Omitido = agora + `futureHorizonWeeks`
   * semanas (até onde projetar as renovações futuras).
   */
  to: z.number().int().optional(),
  /** Horizonte de projeção à frente quando `to` é omitido. Default 52. */
  futureHorizonWeeks: z.number().int().min(1).max(260).default(52),
});
export type ComputeSubscriptionReceivablesInput = z.infer<
  typeof ComputeSubscriptionReceivablesInputSchema
>;

/* ------------------------------------------------------------------ */
/* Output                                                              */
/* ------------------------------------------------------------------ */

/**
 * Totais de recebível numa dimensão (semana ou janela inteira), em cents.
 * `forecastCents = forecastMonthlyCents + forecastYearlyCents` (split por
 * ciclo alimenta a aba "Por ciclo"). `subscriptionCount` é o nº de cobranças
 * previstas naquela dimensão (≈ assinaturas que renovam ali).
 */
export type ReceivablesTotals = {
  forecastCents: number;
  forecastMonthlyCents: number;
  forecastYearlyCents: number;
  realizedCents: number;
  outstandingCents: number;
  subscriptionCount: number;
};

/** Uma semana ISO (segunda→domingo, corte UTC). Buckets são densos: toda
 * semana da janela existe, mesmo zerada (o usuário quer ver "todas as
 * semanas"). */
export type ReceivablesWeekBucket = {
  /** Chave da semana: `YYYY-MM-DD` da segunda-feira (UTC). */
  periodKey: string;
  /** Início da semana (epoch ms, UTC, segunda 00:00). */
  periodStart: number;
  /** true se a semana é futura relativa a `now` (previsto). */
  isForecast: boolean;
  /** true se é a semana que contém `now` (semana atual: realizado + previsto). */
  isCurrent: boolean;
  /** Recebível da semana, por moeda. */
  byCurrency: Record<string, ReceivablesTotals>;
};

export type ComputeSubscriptionReceivablesResult = {
  /** Início efetivo da janela (epoch ms, segunda-feira da 1ª semana). */
  windowStart: number;
  /** Fim efetivo da janela (epoch ms). */
  windowEnd: number;
  /** Relógio do servidor no cálculo (epoch ms) — divisor passado/futuro. */
  now: number;
  /** Semanas da janela, densas, ordenadas por `periodStart`. */
  buckets: ReceivablesWeekBucket[];
  /** Soma sobre a janela inteira, por moeda. */
  totalsByCurrency: Record<string, ReceivablesTotals>;
  /** MRR atual (normalizado p/ mensal) das assinaturas ACTIVE, por moeda. */
  mrrByCurrency: Record<string, number>;
  /** Nº de assinaturas ACTIVE consideradas no previsto. */
  activeSubscriptions: number;
  /** true se a varredura de pagamentos/faturas bateu o teto (há dados não
   * considerados — a UI avisa). */
  capped: boolean;
};
